import { AnimatePresence, motion, type Variants } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Text, VStack } from '@bonusly/design-system';
import { useCurrentCompany, useIsBonuslyCompany } from '@bonusly/who-am-i';

import { GithubConnectCard } from '../github/github-connect-card';
import { GithubManagementView } from '../github/github-management-view';
import { GranolaConnectCard } from '../granola/granola-connect-card';
import { LinearConnectCard } from '../linear/linear-connect-card';
import { SlackConnectCard } from '../slack/slack-connect-card';
import type { BizySettingsSecondaryViewHeaderProps } from './bizy-settings-secondary-view';

type ConnectorView = 'list' | 'github';
type ConnectorViewDirection = 'forward' | 'back';

const VIEW_TRANSITION = { duration: 0.25, ease: [0.4, 0, 0.2, 1] } as const;

const getViewOffset = (direction: ConnectorViewDirection) => (direction === 'forward' ? 24 : -24);

const viewVariants: Variants = {
	initial: (direction: ConnectorViewDirection) => ({ opacity: 0, x: getViewOffset(direction) }),
	animate: { opacity: 1, x: 0 },
	exit: (direction: ConnectorViewDirection) => ({ opacity: 0, x: -getViewOffset(direction) }),
};

interface ConnectorsSectionProps {
	onSecondaryViewHeaderChange?: (header: BizySettingsSecondaryViewHeaderProps | null) => void;
}

export const ConnectorsSection = ({ onSecondaryViewHeaderChange }: ConnectorsSectionProps) => {
	const { t } = useTranslation('bizy_settings');
	const company = useCurrentCompany();
	const isBonuslyCompany = useIsBonuslyCompany();
	const [connectorView, setConnectorView] = useState<ConnectorView>('list');
	const [viewDirection, setViewDirection] = useState<ConnectorViewDirection>('forward');

	useEffect(() => {
		return () => onSecondaryViewHeaderChange?.(null);
	}, [onSecondaryViewHeaderChange]);

	const handleOpenGithubManagement = () => {
		setViewDirection('forward');
		setConnectorView('github');
		onSecondaryViewHeaderChange?.({ backLabel: t('github.management.back'), onBack: handleBackToList });
	};

	const handleBackToList = () => {
		setViewDirection('back');
		setConnectorView('list');
		onSecondaryViewHeaderChange?.(null);
	};

	const hasGranola = company?.bizy_connectors_granola;
	const hasLinear = company?.bizy_connectors_linear;
	const hasGithub = company?.bizy_connectors_github;
	const hasSlack = company?.bizy_connectors_slack;

	if (!hasGranola && !hasLinear && !hasGithub && !hasSlack) return null;

	return (
		<Box overflow="hidden">
			<AnimatePresence custom={viewDirection} initial={false} mode="wait">
				<motion.div
					key={connectorView}
					custom={viewDirection}
					variants={viewVariants}
					initial="initial"
					animate="animate"
					exit="exit"
					transition={VIEW_TRANSITION}
				>
					{connectorView === 'github' && hasGithub && isBonuslyCompany ? (
						<GithubManagementView onBack={handleBackToList} />
					) : (
						<Box>
							<Box pb={3} borderBottom="1px solid" borderColor="border.soft">
								<Text size="md" color="text.softest">
									{t('sectionIntro.connections')}
								</Text>
							</Box>
							<VStack spacing={0} align="stretch" minH="260px">
								{hasGranola && <GranolaConnectCard />}
								{hasLinear && <LinearConnectCard />}
								{hasGithub && <GithubConnectCard onManageRepositories={isBonuslyCompany ? handleOpenGithubManagement : undefined} />}
								{hasSlack && <SlackConnectCard />}
							</VStack>
						</Box>
					)}
				</motion.div>
			</AnimatePresence>
		</Box>
	);
};
